import { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import LaunchOutlinedIcon from "@mui/icons-material/LaunchOutlined";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import { Navigate, useParams } from "react-router-dom";
import PropTypes from "prop-types";
import dayjs from "dayjs";

import CounsellorAuthContext from "../../context/CounsellorAuthContext";
import CounsellorDashboardLayout from "./CounsellorDashboardLayout";
import CalendarSection from "./CalendarSection";
import DashboardBookingsTable from "./DashboardBookingsTable";
import EditProfileDialog from "./EditProfileDialog";
import MeetLinkCell from "./MeetLinkCell";
import ReportLinkCell from "./ReportLinkCell";
import {
  fetchCounsellorPortalBookings,
  fetchCounsellorPortalDashboard,
  fetchCounsellorPortalProfile,
} from "../../services/counsellorPortalApi";
import { useToast } from "../../hook/useToast";

const SECTION_LABELS = {
  dashboard: "Welcome to Counsellor Dashboard",
  calendar: "Calendar",
  bookings: "Bookings",
  payments: "Payments",
  profile: "Profile",
};

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || window.location.origin;

const normalizeMediaUrl = (value) => {
  if (!value) return "";

  const url = String(value);
  if (/^(https?:|blob:|data:)/i.test(url)) return url;

  try {
    const origin = new URL(API_BASE_URL).origin;
    return new URL(url, `${origin}/`).href;
  } catch {
    return url;
  }
};

const RecordsStatCard = ({
  studentLabel,
  studentValue,
  earningLabel,
  earningValue,
  icon: Icon,
  variant = "default",
}) => {
  const styles = {
    navy: {
      background: "#F5F6FA",
      labelColor: "#687083",
      studentColor: "#111827",
      earningLabelColor: "#687083",
      earningValueColor: "#111827",
      iconWrapBg: "#FFFFFF",
      iconColor: "#111827",
      accent: false,
    },
    cream: {
      background: "#FFF7EC",
      labelColor: "#7A6A52",
      studentColor: "#1F1B16",
      earningLabelColor: "#7A6A52",
      earningValueColor: "#1F1B16",
      iconWrapBg: "#FFFFFF",
      iconColor: "#1F1B16",
      accent: false,
    },
    blue: {
      background: "#EEF2F7",
      labelColor: "#475467",
      studentColor: "#0F172A",
      earningLabelColor: "#475467",
      earningValueColor: "#0F172A",
      iconWrapBg: "#FFFFFF",
      iconColor: "#0F172A",
      accent: false,
    },
  };

  const palette = styles[variant] || styles.default || styles.cream;

  return (
    <Paper
      elevation={0}
      sx={{
        position: "relative",
        overflow: "hidden",
        borderRadius: "14px",
        p: { xs: 2.25, md: 2.75 },
        minHeight: { xs: 150, md: 168 },
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        gap: 1.25,
        background: palette.background,
        border: "1px solid rgba(15, 23, 42, 0.04)",
        boxShadow: palette.accent
          ? "0 18px 38px rgba(31, 35, 71, 0.18)"
          : "0 8px 22px rgba(45, 49, 95, 0.06)",
      }}
    >
      {palette.accent ? (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            background:
              "radial-gradient(60% 60% at 110% 110%, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 60%)",
          }}
        />
      ) : null}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <Typography
          sx={{
            fontSize: 15,
            fontWeight: 600,
            color: palette.labelColor,
            letterSpacing: 0.1,
          }}
        >
          {studentLabel}
        </Typography>

        {Icon ? (
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: "10px",
              bgcolor: palette.iconWrapBg,
              color: palette.iconColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              boxShadow: palette.accent
                ? "0 6px 16px rgba(0, 0, 0, 0.18)"
                : "0 4px 10px rgba(15, 23, 42, 0.06)",
            }}
          >
            <Icon sx={{ fontSize: 24 }} />
          </Box>
        ) : null}
      </Box>

      <Box>
        <Typography
          sx={{
            fontSize: { xs: 28, md: 32 },
            fontWeight: 800,
            color: palette.studentColor,
            lineHeight: 1.1,
            letterSpacing: -0.4,
          }}
        >
          {studentValue ?? 0}
        </Typography>

        {earningLabel ? <Box sx={{ mt: 1.25 }}>
          <Typography
            sx={{
              fontSize: 13,
              color: palette.earningLabelColor,
              fontWeight: 600,
            }}
          >
            {earningLabel}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: 18, md: 20 },
              color: palette.earningValueColor,
              fontWeight: 700,
              mt: 0.25,
            }}
          >
            {earningValue}
          </Typography>
        </Box> : null}
      </Box>
    </Paper>
  );
};

RecordsStatCard.propTypes = {
  studentLabel: PropTypes.string.isRequired,
  studentValue: PropTypes.number,
  earningLabel: PropTypes.string,
  earningValue: PropTypes.node,
  icon: PropTypes.elementType,
  variant: PropTypes.oneOf(["navy", "cream", "blue"]),
};

const formatIndianRupee = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return "₹ 0";

  return `₹ ${amount.toLocaleString("en-IN")}`;
};

const detectTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata";
  } catch {
    return "Asia/Kolkata";
  }
};

const ComingSoonSection = ({ title }) => (
  <Box>
    <Typography sx={{ fontSize: 21, fontWeight: 700, color: "#171725", mb: 1.4 }}>
      {title}
    </Typography>

    <Paper
      elevation={0}
      sx={{
        borderRadius: "8px",
        bgcolor: "#fff",
        border: "1px solid #EFEFF4",
        p: 3,
      }}
    >
      <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#171725" }}>
        {title} module
      </Typography>
      <Typography sx={{ fontSize: 14, color: "#667085", mt: 1 }}>
        This section is ready for the next implementation step.
      </Typography>
    </Paper>
  </Box>
);

ComingSoonSection.propTypes = {
  title: PropTypes.string.isRequired,
};

const STATUS_CHIP_COLORS = {
  pending: { bgcolor: "#FFF6E5", color: "#B54708" },
  confirmed: { bgcolor: "#E6F4EA", color: "#1A7F37" },
  completed: { bgcolor: "#EAF1FF", color: "#1F4FB6" },
  cancelled: { bgcolor: "#FDECEC", color: "#B42318" },
  failed: { bgcolor: "#F4F3FF", color: "#5925DC" },
};

const getStatusChipColors = (status) =>
  STATUS_CHIP_COLORS[(status || "").toLowerCase()] || {
    bgcolor: "#F1F2F4",
    color: "#475467",
  };

const formatStatusLabel = (status) => {
  const normalized = String(status || "").trim();
  if (!normalized) return "--";

  return normalized
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const BOOKINGS_COLUMNS = [
  { id: "sr", label: "Sr No", align: "left", width: 70 },
  { id: "name", label: "Student Name", align: "left", width: 220 },
  { id: "city", label: "City", align: "left", width: 150 },
  { id: "date_time", label: "Date & Time", align: "left", width: 200 },
  { id: "gmail_id", label: "Gmail ID", align: "left", width: 220 },
  { id: "report", label: "Report Link", align: "center", width: 110 },
  { id: "meet", label: "Meet Link", align: "center", width: 110 },
  { id: "status", label: "Status", align: "center", width: 110 },
  { id: "completion", label: "Completion", align: "center", width: 120 },
  { id: "booking_month", label: "Booking Month", align: "center", width: 140 },
];

const monthLabel = (date) => date.format("MMMM YYYY");

const buildMonthOptions = (count = 24) => {
  const base = dayjs();
  const options = [{ value: "", label: "-- Select Month --" }];

  for (let offset = 0; offset < count; offset += 1) {
    const date = base.subtract(offset, "month");
    options.push({
      value: date.format("YYYY-MM"),
      label: monthLabel(date),
    });
  }

  return options;
};

const getInitials = (name = "") => {
  const trimmed = name.trim();
  if (!trimmed) return "?";

  const [first, second] = trimmed.split(/\s+/);
  const initials = `${first?.[0] || ""}${second?.[0] || ""}`.toUpperCase();
  return initials || "?";
};

const valueOrDash = (value) => {
  if (value === undefined || value === null || String(value).trim() === "") {
    return "--";
  }

  return value;
};

const normalizeExternalUrl = (value) => {
  const url = String(value || "").trim();
  if (!url) return "";

  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
};

const getCertificateUrl = (certificate) => {
  if (!certificate) return "";

  if (typeof certificate === "object") {
    return normalizeMediaUrl(certificate.file || certificate.url || certificate.certificate || "");
  }

  return normalizeMediaUrl(certificate);
};

const getCertificateName = (certificate, index) => {
  if (!certificate) return `Certificate ${index + 1}`;

  if (typeof certificate === "object") {
    const fileUrl = certificate.file || certificate.url || certificate.certificate || "";
    return (
      certificate.name ||
      certificate.original_name ||
      String(fileUrl).split("/").pop() ||
      `Certificate ${index + 1}`
    );
  }

  return String(certificate).split("/").pop() || `Certificate ${index + 1}`;
};

const getProfileCertificates = (profile) => {
  const certificates = Array.isArray(profile?.certificates)
    ? profile.certificates
        .map((certificate, index) => ({
          id: certificate?.certificate_id || certificate?.id || `certificate-${index}`,
          name: getCertificateName(certificate, index),
          url: getCertificateUrl(certificate),
          uploaded_at: certificate?.uploaded_at || "",
        }))
        .filter((certificate) => certificate.url)
    : [];

  const legacyCertificateUrl = getCertificateUrl(profile?.certificate);
  if (
    legacyCertificateUrl &&
    !certificates.some((certificate) => certificate.url === legacyCertificateUrl)
  ) {
    certificates.unshift({
      id: "legacy-certificate",
      name: getCertificateName(profile.certificate, 0),
      url: legacyCertificateUrl,
      uploaded_at: "",
    });
  }

  return certificates;
};

const DetailHeading = ({ children }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.75, mb: 1.5 }}>
    <Typography
      sx={{
        color: "#171725",
        fontSize: 14,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {children}
    </Typography>
    <Divider sx={{ flex: 1, borderColor: "#E4E7EC" }} />
  </Box>
);

DetailHeading.propTypes = {
  children: PropTypes.node.isRequired,
};

const ProfileInfoField = ({ label, value, href, multiline = false }) => {
  const displayValue = valueOrDash(value);
  const isLinked = Boolean(href && displayValue !== "--");

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "155px minmax(0, 1fr)" },
        alignItems: multiline ? "flex-start" : "center",
        columnGap: 2,
        rowGap: 0.6,
        minHeight: multiline ? "auto" : 28,
      }}
    >
      <Typography sx={{ fontSize: 13, color: "#667085", lineHeight: 1.6 }}>
        {label} :
      </Typography>

      <Typography
        component={isLinked ? "a" : "span"}
        href={isLinked ? href : undefined}
        target={isLinked ? "_blank" : undefined}
        rel={isLinked ? "noopener noreferrer" : undefined}
        sx={{
          color: isLinked ? "#1F4FB6" : "#111827",
          fontSize: 13.5,
          fontWeight: 500,
          lineHeight: 1.55,
          textDecoration: "none",
          wordBreak: "break-word",
          whiteSpace: multiline ? "pre-line" : "normal",
          "&:hover": isLinked ? { textDecoration: "underline" } : undefined,
        }}
      >
        {displayValue}
      </Typography>
    </Box>
  );
};

ProfileInfoField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  href: PropTypes.string,
  multiline: PropTypes.bool,
};

const CertificateRow = ({ certificate, index }) => {
  const uploadedAt = certificate.uploaded_at ? dayjs(certificate.uploaded_at) : null;
  const uploadedLabel =
    uploadedAt && uploadedAt.isValid() ? uploadedAt.format("DD MMM YYYY") : "";

  return (
    <Paper
      component="a"
      href={certificate.url}
      target="_blank"
      rel="noopener noreferrer"
      elevation={0}
      sx={{
        p: 1.5,
        borderRadius: "8px",
        border: "1px solid #EFEFF4",
        bgcolor: "#fff",
        textDecoration: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        "&:hover": {
          borderColor: "#C9D7FE",
          bgcolor: "#FBFCFF",
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, gap: 1.25 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "8px",
            bgcolor: "#FFF6E5",
            color: "#B54708",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <WorkspacePremiumOutlinedIcon sx={{ fontSize: 20 }} />
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 700,
              color: "#171725",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {certificate.name || `Certificate ${index + 1}`}
          </Typography>
          {uploadedLabel ? (
            <Typography sx={{ fontSize: 12, color: "#667085", mt: 0.25 }}>
              Uploaded {uploadedLabel}
            </Typography>
          ) : null}
        </Box>
      </Box>

      <LaunchOutlinedIcon sx={{ fontSize: 18, color: "#1F4FB6", flexShrink: 0 }} />
    </Paper>
  );
};

CertificateRow.propTypes = {
  certificate: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    url: PropTypes.string.isRequired,
    uploaded_at: PropTypes.string,
  }).isRequired,
  index: PropTypes.number.isRequired,
};

const BookingsSection = () => {
  const { showToast } = useToast();

  const [bookings, setBookings] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 20,
    total_records: 0,
    total_pages: 0,
  });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [month, setMonth] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [filterAnchor, setFilterAnchor] = useState(null);

  const requestIdRef = useRef(0);
  const monthOptions = useMemo(() => buildMonthOptions(24), []);

  useEffect(() => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);

    fetchCounsellorPortalBookings({
      page,
      pageSize,
      status: statusFilter,
      search: appliedSearch,
      month,
    })
      .then((response) => {
        if (requestId !== requestIdRef.current) return;

        const payload = response?.data?.data || {};
        setBookings(payload.bookings || []);
        setPagination(
          payload.pagination || {
            page,
            page_size: pageSize,
            total_records: 0,
            total_pages: 0,
          },
        );
      })
      .catch((error) => {
        if (requestId !== requestIdRef.current) return;

        const apiMessage =
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          error?.message ||
          "Could not load bookings. Please try again.";

        showToast(apiMessage, "error");
        setBookings([]);
        setPagination({
          page,
          page_size: pageSize,
          total_records: 0,
          total_pages: 0,
        });
      })
      .finally(() => {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      });
  }, [page, pageSize, statusFilter, appliedSearch, month, showToast]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setPage(1);
      setAppliedSearch(search.trim());
    }, 300);

    return () => clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [month]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  const handlePageChange = (_event, value) => {
    if (value !== page) setPage(value);
  };

  const handleMonthChange = (event) => {
    setMonth(event.target.value);
  };

  const handleFilterClick = (event) => {
    setFilterAnchor(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchor(null);
  };

  const handleStatusSelect = (value) => {
    setStatusFilter(value === "all" ? "" : value);
    setFilterAnchor(null);
  };

  const STATUS_FILTER_OPTIONS = [
    { value: "all", label: "All statuses" },
    { value: "pending", label: "Pending" },
    { value: "confirmed", label: "Confirmed" },
    { value: "completed", label: "Completed" },
    { value: "cancelled", label: "Cancelled" },
    { value: "failed", label: "Failed" },
  ];

  const handleClearMonth = () => {
    setMonth("");
  };

  const totalRecords = pagination.total_records || 0;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Typography sx={{ fontSize: 22, fontWeight: 700, color: "#171725" }}>
          Student Record
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Select
            value={month}
            onChange={handleMonthChange}
            displayEmpty
            size="small"
            sx={{
              minWidth: 200,
              bgcolor: "#fff",
              borderRadius: "8px",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#E4E4EB",
              },
              "& fieldset": { borderRadius: "8px" },
            }}
            renderValue={(selected) => {
              if (!selected) {
                return (
                  <Typography sx={{ fontSize: 14, color: "#92929D" }}>
                    -- Select Month --
                  </Typography>
                );
              }
              const matched = monthOptions.find((opt) => opt.value === selected);
              return (
                <Typography sx={{ fontSize: 14, color: "#171725" }}>
                  {matched ? matched.label : selected}
                </Typography>
              );
            }}
          >
            {monthOptions.map((option) => (
              <MenuItem key={option.value || "all"} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>

          <TextField
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search…"
            sx={{
              width: 220,
              bgcolor: "#fff",
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                "& fieldset": { borderColor: "#E4E4EB" },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon sx={{ fontSize: 18, color: "#92929D" }} />
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant="outlined"
            startIcon={<FilterAltOutlinedIcon sx={{ fontSize: 18 }} />}
            onClick={handleFilterClick}
            sx={{
              textTransform: "none",
              borderColor: "#E4E4EB",
              color: "#171725",
              bgcolor: "#fff",
              borderRadius: "8px",
              px: 1.75,
              "&:hover": { borderColor: "#C9C9D4", bgcolor: "#FAFAFC" },
            }}
          >
            Filter
            {statusFilter ? ` · ${statusFilter}` : ""}
          </Button>

          <Menu
            anchorEl={filterAnchor}
            open={Boolean(filterAnchor)}
            onClose={handleFilterClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 180,
                borderRadius: "8px",
                border: "1px solid #EFEFF4",
                boxShadow: "0 18px 48px rgba(45, 49, 95, 0.12)",
              },
            }}
          >
            {STATUS_FILTER_OPTIONS.map((option) => (
              <MenuItem
                key={option.value}
                selected={statusFilter === option.value || (option.value === "all" && !statusFilter)}
                onClick={() => handleStatusSelect(option.value)}
                sx={{ fontSize: 14, color: "#171725" }}
              >
                {option.label}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: "10px",
          border: "1px solid #EFEFF4",
          bgcolor: "#fff",
          overflow: "hidden",
        }}
      >
        <TableContainer>
          <Table sx={{ minWidth: 1120 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: "#FAFAFC" }}>
                {BOOKINGS_COLUMNS.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    sx={{
                      width: column.width,
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#475467",
                      borderBottom: "1px solid #EFEFF4",
                      py: 1.5,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      {column.label}
                      <ArrowForwardIosIcon
                        sx={{ fontSize: 10, color: "#98A2B3", transform: "rotate(90deg)" }}
                      />
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {!loading && bookings.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={BOOKINGS_COLUMNS.length}
                    sx={{
                      py: 6,
                      textAlign: "center",
                      borderBottom: "none",
                      color: "#667085",
                      fontSize: 14,
                    }}
                  >
                    No bookings found for the selected filters.
                  </TableCell>
                </TableRow>
              )}

              {loading && bookings.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={BOOKINGS_COLUMNS.length}
                    sx={{
                      py: 6,
                      textAlign: "center",
                      borderBottom: "none",
                      color: "#667085",
                      fontSize: 14,
                    }}
                  >
                    Loading bookings…
                  </TableCell>
                </TableRow>
              )}

              {bookings.map((booking) => {
                const statusLabel = formatStatusLabel(booking.status);
                const chipStyles = getStatusChipColors(booking.status);
                const completionLabel = formatStatusLabel(
                  booking.completion_status || "pending",
                );
                const completionStyles = getStatusChipColors(
                  booking.completion_status || "pending",
                );
                const reportHref = booking.report_link || "";
                const meetHref = booking.google_meet_link || "";

                return (
                  <TableRow
                    key={booking.booking_id}
                    sx={{
                      "& td": { borderBottom: "1px solid #F1F2F4" },
                      "&:last-of-type td": { borderBottom: "none" },
                      "&:hover": { bgcolor: "#FAFAFC" },
                    }}
                  >
                    <TableCell sx={{ py: 1.5, fontSize: 14, color: "#171725", width: 70 }}>
                      {booking.sr_no ?? "—"}
                    </TableCell>

                    <TableCell align="center" sx={{ py: 1.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: "#2D315F",
                            fontSize: 12,
                            fontWeight: 700,
                          }}
                        >
                          {getInitials(booking.student_name)}
                        </Avatar>
                        <Typography sx={{ fontSize: 14, color: "#171725", fontWeight: 500 }}>
                          {booking.student_name || "—"}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell sx={{ py: 1.5, fontSize: 14, color: "#171725" }}>
                      {booking.city || "—"}
                    </TableCell>

                    <TableCell sx={{ py: 1.5, fontSize: 14, color: "#171725", whiteSpace: "nowrap" }}>
                      {booking.date_time || "—"}
                    </TableCell>

                    <TableCell sx={{ py: 1.5, fontSize: 14, color: "#171725" }}>
                      {booking.gmail_id || "—"}
                    </TableCell>

                    <TableCell align="center" sx={{ py: 1.5 }}>
                      <ReportLinkCell
                        href={reportHref}
                        studentName={booking.student_name}
                      />
                    </TableCell>

                    <TableCell align="center" sx={{ py: 1.5 }}>
                      <MeetLinkCell href={meetHref} />
                    </TableCell>

                    <TableCell sx={{ py: 1.5 }}>
                      <Chip
                        label={statusLabel}
                        size="small"
                        sx={{
                          bgcolor: chipStyles.bgcolor,
                          color: chipStyles.color,
                          fontSize: 12,
                          fontWeight: 600,
                          borderRadius: "6px",
                        }}
                      />
                    </TableCell>

                    <TableCell align="center" sx={{ py: 1.5 }}>
                      <Chip
                        label={completionLabel}
                        size="small"
                        sx={{
                          bgcolor: completionStyles.bgcolor,
                          color: completionStyles.color,
                          fontSize: 12,
                          fontWeight: 600,
                          borderRadius: "6px",
                        }}
                      />
                    </TableCell>

                    <TableCell align="center" sx={{ py: 1.5 }}>
                      <Chip
                        label={booking.booking_month || "--"}
                        size="small"
                        sx={{
                          bgcolor: "#F1F2F4",
                          color: "#475467",
                          fontSize: 12,
                          fontWeight: 600,
                          borderRadius: "6px",
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
            px: 2,
            py: 1.5,
            borderTop: "1px solid #EFEFF4",
          }}
        >
          <Typography sx={{ fontSize: 13, color: "#475467" }}>
            Total - {totalRecords}
          </Typography>

          {pagination.total_pages > 1 ? (
            <Pagination
              count={pagination.total_pages}
              page={pagination.page || page}
              onChange={handlePageChange}
              shape="rounded"
              siblingCount={1}
              boundaryCount={1}
              sx={{
                "& .MuiPaginationItem-root.Mui-selected": {
                  bgcolor: "#2D315F",
                  color: "#fff",
                  "&:hover": { bgcolor: "#1F2347" },
                },
              }}
            />
          ) : (
            <Box />
          )}
        </Box>
      </Paper>

      {month && (
        <Button
          variant="text"
          onClick={handleClearMonth}
          sx={{ mt: 1.5, textTransform: "none", color: "#1F4FB6" }}
        >
          Clear month filter
        </Button>
      )}
    </Box>
  );
};

const DashboardSection = () => {
  const { counsellorUser, portal } = useContext(CounsellorAuthContext);
  const { showEarnings } = portal;
  const { showToast } = useToast();
  const initialDashboard = counsellorUser?.dashboard || {};

  const [dashboard, setDashboard] = useState({
    total_students: initialDashboard.total_students || 0,
    students_this_month: initialDashboard.students_this_month || 0,
    students_today: initialDashboard.students_today || 0,
    total_earnings: initialDashboard.total_earnings || 0,
    earnings_this_month: initialDashboard.earnings_this_month || 0,
    earnings_today: initialDashboard.earnings_today || 0,
  });
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);
  const timezone = useMemo(detectTimezone, []);

  useEffect(() => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);

    fetchCounsellorPortalDashboard({ timezone })
      .then((response) => {
        if (requestId !== requestIdRef.current) return;

        const payload = response?.data?.data || {};
        setDashboard((prev) => ({
          ...prev,
          total_students: payload.total_students ?? prev.total_students,
          students_this_month:
            payload.students_this_month ?? prev.students_this_month,
          students_today: payload.students_today ?? prev.students_today,
          total_earnings: payload.total_earnings ?? prev.total_earnings,
          earnings_this_month:
            payload.earnings_this_month ?? prev.earnings_this_month,
          earnings_today: payload.earnings_today ?? prev.earnings_today,
        }));
      })
      .catch((error) => {
        if (requestId !== requestIdRef.current) return;

        const apiMessage =
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          error?.message ||
          "Could not load dashboard. Please try again.";

        showToast(apiMessage, "error");
      })
      .finally(() => {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      });
  }, [showToast, timezone]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#171725" }}>
          Records
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={14} sx={{ color: "#2D315F" }} />
            <Typography sx={{ fontSize: 12, color: "#667085", fontWeight: 600 }}>
              Refreshing…
            </Typography>
          </Box>
        ) : null}
      </Box>

     
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <RecordsStatCard
            studentLabel="Total Students"
            studentValue={dashboard.total_students}
            earningLabel={showEarnings ? "Total Earning" : ""}
            earningValue={formatIndianRupee(dashboard.total_earnings)}
            icon={GroupsRoundedIcon}
            variant="navy"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <RecordsStatCard
            studentLabel="Students this Month"
            studentValue={dashboard.students_this_month}
            earningLabel={showEarnings ? "Earning this month" : ""}
            earningValue={formatIndianRupee(dashboard.earnings_this_month)}
            icon={CalculateOutlinedIcon}
            variant="cream"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <RecordsStatCard
            studentLabel="Students Today"
            studentValue={dashboard.students_today}
            earningLabel={showEarnings ? "Earning Today" : ""}
            earningValue={formatIndianRupee(dashboard.earnings_today)}
            icon={PersonAddAlt1OutlinedIcon}
            variant="blue"
          />
        </Grid>
      </Grid>

      <DashboardBookingsTable />
    </Box>
  );
};

const ProfileSection = () => {
  const { counsellorUser, updateCounsellorProfile } = useContext(CounsellorAuthContext);
  const { showToast } = useToast();
  const [profile, setProfile] = useState(counsellorUser?.counsellor || {});
  const [loading, setLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);

    fetchCounsellorPortalProfile()
      .then((response) => {
        if (requestId !== requestIdRef.current) return;

        setProfile(response?.data?.data || {});
      })
      .catch((error) => {
        if (requestId !== requestIdRef.current) return;

        const apiMessage =
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          error?.message ||
          "Could not load profile. Please try again.";

        showToast(apiMessage, "error");
      })
      .finally(() => {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      });
  }, [showToast]);

  const displayName = profile.name || profile.full_name || "Counsellor";
  const email = profile.email || counsellorUser?.userInfo?.email || "";
  const photoUrl = normalizeMediaUrl(profile.photo);
  const linkedinUrl = normalizeExternalUrl(profile.linkedin);
  const certificates = useMemo(() => getProfileCertificates(profile), [profile]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography sx={{ fontSize: 24, fontWeight: 800, color: "#171725" }}>
        Profile
      </Typography>

      <Paper
        elevation={0}
        sx={{
          borderRadius: "8px",
          border: "1px solid #DDE1EA",
          bgcolor: "#fff",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            height: { xs: 132, md: 152 },
            backgroundImage: "url('/assets/newImages/Backdrop.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />

        {loading ? (
          <Box
            sx={{
              position: "absolute",
              top: 16,
              right: 18,
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: "#667085",
            }}
          >
            <CircularProgress size={18} sx={{ color: "#2D315F" }} />
            <Typography sx={{ fontSize: 12, fontWeight: 700 }}>Loading</Typography>
          </Box>
        ) : null}

        <Box
          sx={{
            position: "relative",
            minHeight: { xs: 220, md: 164 },
            px: { xs: 2.25, md: 3.75 },
            pt: { xs: 7.8, md: 7.3 },
            pb: 2.4,
          }}
        >
          <Avatar
            src={photoUrl || undefined}
            sx={{
              position: "absolute",
              top: { xs: -54, md: -68 },
              left: { xs: 22, md: 36 },
              width: { xs: 108, md: 124 },
              height: { xs: 108, md: 124 },
              borderRadius: "10px",
              border: "4px solid #fff",
              bgcolor: "#2D315F",
              color: "#fff",
              fontSize: 34,
              fontWeight: 800,
              boxShadow: "0 18px 40px rgba(45, 49, 95, 0.18)",
            }}
          >
            {!photoUrl ? getInitials(displayName) : null}
          </Avatar>

          <Box
            sx={{
              display: "flex",
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box sx={{ minWidth: 0, maxWidth: 720 }}>
              <Typography
                sx={{
                  fontSize: { xs: 26, md: 30 },
                  fontWeight: 500,
                  color: "#111827",
                  lineHeight: 1.15,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                @{displayName}
              </Typography>

              <Typography
                sx={{
                  fontSize: 14,
                  color: "#6B7280",
                  mt: 0.75,
                  fontWeight: 700,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {email || "--"}
              </Typography>

              <Typography
                sx={{
                  fontSize: 13,
                  color: "#667085",
                  mt: 0.8,
                  lineHeight: 1.55,
                  maxWidth: 780,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {profile.description || "No description available."}
              </Typography>
            </Box>

            {certificates.length ? (
              <Chip
                label={`${certificates.length} certificate${certificates.length === 1 ? "" : "s"}`}
                size="small"
                sx={{
                  alignSelf: { xs: "flex-end", md: "center" },
                  borderRadius: "6px",
                  bgcolor: "#FFF6E5",
                  color: "#B54708",
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              />
            ) : null}

            <Button
              variant="contained"
              startIcon={<EditOutlinedIcon sx={{ fontSize: 18 }} />}
              onClick={() => setEditDialogOpen(true)}
              sx={{
                alignSelf: { xs: "flex-start", md: "center" },
                textTransform: "none",
                bgcolor: "#FFA447",
                color: "#fff",
                fontWeight: 700,
                fontSize: 15,
                borderRadius: "999px",
                px: 2.5,
                py: 0.9,
                boxShadow: "none",
                flexShrink: 0,
                whiteSpace: "nowrap",
                "&:hover": { bgcolor: "#F5912E", boxShadow: "none" },
              }}
            >
              Edit / Update
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            height: 54,
            borderTop: "1px solid #DDE1EA",
            display: "flex",
            alignItems: "flex-end",
            px: { xs: 2.25, md: 5.7 },
          }}
        >
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              borderBottom: "3px solid #FFA447",
              px: 0.35,
            }}
          >
            <Typography
              sx={{
                color: "#292E5C",
                fontSize: 14,
                fontWeight: 800,
              }}
            >
              Personal Information
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.25, md: 1.4 },
          borderRadius: "8px",
          border: "none",
          bgcolor: "#EDEEF1",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 2.4 },
            borderRadius: "8px",
            border: "1px solid #DDE1EA",
            bgcolor: "#fff",
          }}
        >
          <DetailHeading>Personal Information</DetailHeading>

          <Grid container rowSpacing={1.15} columnSpacing={5} sx={{ mb: 3.25 }}>
            <Grid item xs={12} md={6}>
              <ProfileInfoField label="Full Name" value={profile.full_name || profile.name} />
            </Grid>
            <Grid item xs={12} md={6}>
              <ProfileInfoField label="Email" value={email} />
            </Grid>
            <Grid item xs={12} md={6}>
              <ProfileInfoField label="Phone No." value={profile.phone_number} />
            </Grid>
            <Grid item xs={12} md={6}>
              <ProfileInfoField
                label="Year of experience"
                value={profile.years_of_experience}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ProfileInfoField label="City" value={profile.city} />
            </Grid>
          </Grid>

          <DetailHeading>Contact Information</DetailHeading>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, mb: 3.2 }}>
            <ProfileInfoField
              label="Linked in"
              value={profile.linkedin}
              href={linkedinUrl}
            />
            <ProfileInfoField
              label="Description"
              value={profile.description || "No description available."}
              multiline
            />
          </Box>

          <DetailHeading>Certificates</DetailHeading>

          {certificates.length ? (
            <Grid container spacing={1.3}>
              {certificates.map((certificate, index) => (
                <Grid item xs={12} md={6} key={certificate.id || certificate.url}>
                  <CertificateRow certificate={certificate} index={index} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography sx={{ fontSize: 13.5, color: "#98A2B3" }}>
              No certificates available.
            </Typography>
          )}
        </Paper>
      </Paper>

      <EditProfileDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSuccess={(updatedProfile) => {
          if (updatedProfile && typeof updatedProfile === "object") {
            setProfile(updatedProfile);
            updateCounsellorProfile?.(updatedProfile);
          }
          showToast("Profile updated successfully.", "success");
        }}
        initialValues={profile}
      />
    </Box>
  );
};

const CounsellorDashboardPage = () => {
  const { section = "dashboard" } = useParams();
  const { counsellorUser, portal } = useContext(CounsellorAuthContext);
  const profileTitleName =
    counsellorUser?.counsellor?.name ||
    counsellorUser?.counsellor?.full_name ||
    counsellorUser?.userInfo?.name ||
    "Counsellor";

  if (
    section === "portal" ||
    !SECTION_LABELS[section] ||
    (section === "payments" && !portal.showEarnings)
  ) {
    return <Navigate to={`${portal.basePath}/dashboard`} replace />;
  }

  const sectionTitle =
    section === "dashboard" ? `Welcome to ${portal.label} Dashboard` : SECTION_LABELS[section];

  return (
    <CounsellorDashboardLayout
      title={section === "profile" ? `Welcome ${profileTitleName}` : sectionTitle}
    >
      {section === "dashboard" ? (
        <DashboardSection />
      ) : section === "calendar" ? (
        <CalendarSection />
      ) : section === "bookings" ? (
        <BookingsSection />
      ) : section === "profile" ? (
        <ProfileSection />
      ) : (
        <ComingSoonSection title={SECTION_LABELS[section]} />
      )}
    </CounsellorDashboardLayout>
  );
};

export default CounsellorDashboardPage;
